import { FormSectionProps } from '../../../../common/interfaces/formSectionProps';
import { EMPTY_STRING } from '../../../../common/constants/grimsby';
import {
    ActivityData,
    CreateActivityData,
    DeliveryInstructor,
    DeliverySession,
} from '../../../interfaces/activity';
import { ValidationType } from '../../../../common/utils/formValidation';
import { CourseItem } from '../../../../imt/interfaces/instructorProfile';
import { MeridiemFieldValue } from '../../../interfaces/meridiem';
import { convertTimeStampToLocalUnix } from '../../../../common/utils/date-time.utils';
import { BlockedTime } from '../../../../common/interfaces/blockedTime';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(timezone);

export enum AudienceType {
    Public = 'Public',
    PublicUnlisted = 'Public Unlisted',
    PartnerPublic = 'Partner Public',
    PartnerPublicUnlisted = 'Partner Public Unlisted',
    CommercialPrivate = 'Commercial Private',
    PartnerPrivate = 'Partner Private',
    Internal = 'Internal',
    InternalPublic = 'Internal Public',
    InternalPrivate = 'Internal Private',
    CommercialSemiPrivate = 'Commercial Semi-Private',
    PartnerSemiPrivate = 'Partner Semi-Private',
}

export enum ActivityType {
    Bootcamp = 'Bootcamp',
    Class = 'Class',
    ClassBoltOnCloudQuest = 'Class (bolt on with Cloud Quest)',
    Event = 'Delivery driven event',
    TTT = 'TTT', //Train The Trainer
    Webinar = 'Webinar',
}

export enum ActivityStatus {
    Active = 'Active',
    Canceled = 'Canceled',
    Completed = 'Completed',
    Hold = 'Hold',
    Tentative = 'Tentative',
}

export enum ActivityModality {
    ILT = 'ILT',
    Hybrid = 'Hybrid',
    vILT = 'vILT',
}

export enum ActivityProgram {
    Partner = 'Partner Program',
    Commercial = 'Commercial',
    ChannelPending = 'Channel Pending',
    SkillsCenters = 'Skills Centers',
    WorldWideFieldEnablement = 'World Wide Field Enablement',
    PartnerReseller = 'Partner Reseller',
    AAI = 'AWS Authorized Instructor (AAI)',
    DedicatedCloud = 'Dedicated Cloud',
}

export enum ActivityLMSType {
    MyClass = 'myClass',
    Kiku = 'Kiku',
    CustomerLMS = 'CustomerLMS',
    SkillBuilder = 'SkillBuilder',
    None = 'None',
}

export enum ActivityViltType {
    Necto = 'Necto',
    Other = 'Other',
    WebEx = 'WebEx',
}

export enum RegionShortName {
    ANZ = 'Australia/New Zealand (ANZ)',
    ASEAN = 'Southeast Asia (ASEAN)',
    BENELUX = 'Belgium/Netherlands/Luxembourg (BENELUX)',
    CHINA = 'China',
    DACH = 'Germany/Austria/Switzerland (DACH)',
    EEM = 'Eastern European Markets (EEM)',
    FRANCE = 'France',
    HKT = 'Hong Kong/Taiwan (HKT)',
    IBERIA = 'Spain/Portugal (IBERIA)',
    INDIA = 'India',
    ISRAEL = 'Israel',
    ITALY = 'Italy',
    JAPAN = 'Japan',
    KOREA = 'Korea',
    NORDICS = 'Denmark/Finland/Norway/Sweden (NORDICS)',
    UKIR = 'UK/Ireland (UKIR)',
    LATAM = 'Latin America (LATAM)',
    NAMER = 'North American (NAMER)',
}

export enum WaitlistEnabled {
    Disabled = 'Disabled',
    Automatic = 'Automatic',
    Manual = 'Manual',
}

export interface SessionAttributeEditorItem {
    deliverySessionId?: string;
    dateString: string;
    startTime: string;
    endTime: string;
    endTimeMeridiemFieldOption: MeridiemFieldValue;
    startTimeMeridiemFieldOption: MeridiemFieldValue;
    instructors?: Array<DeliveryInstructor>;
}

/**
 * Builds the blank session row seeded by every "Add date" button.
 *
 * The end meridiem must default to PM. Once a date is picked,
 * `handleSessionDateChange` backfills blank times with
 * `endTimeMeridiemFieldOption ?? MeridiemFieldValue.Pm`, and that fallback
 * cannot apply when a value is already set, so defaulting to AM produced
 * sessions running 9:00 AM to 5:00 AM the following morning.
 *
 * @returns A session row with an empty date and times, start meridiem AM and
 *          end meridiem PM.
 */
export const createEmptySessionAttributeEditorItem =
    (): SessionAttributeEditorItem => ({
        dateString: '',
        startTime: '',
        endTime: '',
        startTimeMeridiemFieldOption: MeridiemFieldValue.Am,
        endTimeMeridiemFieldOption: MeridiemFieldValue.Pm,
    });

export interface ActivitySessionControlArrayFormValues {
    sessionAttributeEditorItems: SessionAttributeEditorItem;
}

export interface ActivityInstructorControlArrayFormValues {
    instructorAttributeEditorItems: DeliveryInstructor;
}

export type ActivityControlArrayValues = ActivitySessionControlArrayFormValues &
    ActivityInstructorControlArrayFormValues;

export type ActivityControlArrayErrors<ControlArrayFormValues> = {
    [controlArrayFormValuesKey in keyof ControlArrayFormValues]: {
        [key: number]: {
            [
                key in keyof ControlArrayFormValues[controlArrayFormValuesKey]
            ]?: string;
        };
    };
};

interface ShowAdditionalFieldProps {
    showDeliveryAddress: boolean;
    showNumberOfStudentsAttended: boolean;
    showPartnerInitiative: boolean;
    showLMSType: boolean;
    showClassSize: boolean;
    showVILTType: boolean;
}

export type ActivityFormSectionProps<T> = FormSectionProps<T> & {
    setShowAdditionalFields?: React.Dispatch<
        React.SetStateAction<ShowAdditionalFieldProps>
    >;
    showAdditionalFields?: ShowAdditionalFieldProps;
    handleSessionItemEvent: (
        sessionItems: Array<SessionAttributeEditorItem>,
    ) => void;
    sessionAttributeEditorItems: Array<SessionAttributeEditorItem>;
    // When true, changing the first session's date preserves the existing
    // session times instead of regenerating default times from the course.
    // Set by the clone flow so cloned start/end times carry over; left unset
    // (falsy) for create, where a first-date pick auto-generates sessions.
    preserveSessionTimesOnDateChange?: boolean;
    controlArrayErrors: ActivityControlArrayErrors<ActivitySessionControlArrayFormValues>;
    handleCustomersChange?: (customerIndexes: Array<number>) => void;
    handleInvoicesChange?: (invoiceIndexes: Array<number>) => void;
    handleSubmitEvent?: () => Promise<void>;
};

export interface InstructorAvailabilityData {
    booked_activities: Array<ActivityData>;
    back_to_back_activities: Array<ActivityData>;
    do_not_shuffle_activities?: Array<ActivityData>;
    blocked_times?: Array<BlockedTime>;
    pk: string;
    warning_messages: Array<string>;
    error_messages: Array<string>;
    availability: string;
    full_name: string;
    delivery_languages: Array<string>;
    delivery_countries: Array<string>;
    courses: Array<CourseItem> | null;
}

export interface InstructorAssignmentConflict {
    instructor: InstructorAvailabilityData | null;
    activity: ActivityData | null;
}

export type ActivityDeliveryDetailsFormSectionProps = ActivityFormSectionProps<
    Omit<ActivityData, 'delivery_sessions'>
> & {
    instructorAttributeEditorItems: Array<DeliveryInstructor>;
    handleInstructorItemEvent: (
        instructorItems: Array<DeliveryInstructor>,
    ) => void;
    controlArrayErrors?: ActivityControlArrayErrors<ActivitySessionControlArrayFormValues> &
        ActivityControlArrayErrors<ActivityInstructorControlArrayFormValues>;
    instructorValidationResponse: InstructorAvailabilityData[];
    handleAddInstructor: (instructorPk: string) => void;
    handleSessionInstructorAssignmentEvent?: (
        sessionItems: Array<SessionAttributeEditorItem>,
    ) => void;
    instructorItemErrors?: any;
    areSessionsInvalid?: any;
};

export type ActivitySFDCDeliveryDetailsFormSectionProps = Omit<
    ActivityFormSectionProps<Omit<ActivityData, 'delivery_sessions'>> & {
        instructorAttributeEditorItems: Array<DeliveryInstructor>;
        handleInstructorItemEvent: (
            instructorItems: Array<DeliveryInstructor>,
        ) => void;
        controlArrayErrors?: ActivityControlArrayErrors<ActivitySessionControlArrayFormValues> &
            ActivityControlArrayErrors<ActivityInstructorControlArrayFormValues>;
        instructorValidationResponse: InstructorAvailabilityData[];
        handleAddInstructor: (instructorPk: string) => void;
        instructorItemErrors?: any;
    },
    'validateAndHandleFieldEvent'
>;

export const currencyFormatter = (amount: any, currency: string) => {
    let formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
    });

    return formatter.format(amount);
};

export type BasicActivityFormSectionProps = Omit<
    ActivityFormSectionProps<Omit<ActivityData, 'delivery_sessions'>>,
    | 'sessionAttributeEditorItems'
    | 'handleSessionItemEvent'
    | 'controlArrayErrors'
    | 'mode'
>;

export const createActivityValidationConfig: {
    [key in ValidationType]?: Array<
        keyof Omit<CreateActivityData, 'delivery_sessions'>
    >;
} = {
    required: [
        'activity_status',
        'activity_type',
        'program',
        'activity_audience',
        'activity_modality',
        'course_name',
        'operations_owner',
        'provider',
        'delivery_timezone',
        'delivery_city',
        'delivery_country',
        'delivery_region',
        'delivery_geo',
        'delivery_language',
    ],
};

export const sessionAttributeValidationConfig: {
    [
        activityControlArrayFormValuesKey in keyof ActivitySessionControlArrayFormValues
    ]: {
        [key in ValidationType]?: Array<
            keyof ActivitySessionControlArrayFormValues[activityControlArrayFormValuesKey]
        >;
    };
} = {
    sessionAttributeEditorItems: {
        required: [
            'dateString',
            'startTime',
            'endTime',
            'endTimeMeridiemFieldOption',
            'startTimeMeridiemFieldOption',
        ],
    },
};

export const getSessionAttributeEditorItem = (
    {
        id,
        start_timestamp: startTimeStamp,
        end_timestamp: endTimeStamp,
        instructors,
    }: DeliverySession,
    deliveryTimeZone: string,
): SessionAttributeEditorItem => {
    const deliverySessionId = id;
    const { time: startTime, meridiem: startTimeMeridiemFieldOption } =
        getHourTimeFormat(startTimeStamp, deliveryTimeZone);
    const { time: endTime, meridiem: endTimeMeridiemFieldOption } =
        getHourTimeFormat(endTimeStamp, deliveryTimeZone);
    const startDate = startTimeStamp
        ? new Date(startTimeStamp * 1000)
        : new Date();
    const localeSessionDate = startDate.toLocaleDateString('en-US', {
        timeZone: deliveryTimeZone,
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    });
    const dateParts = localeSessionDate.split('/');
    return {
        deliverySessionId: deliverySessionId ?? '',
        startTime: startTime ?? '',
        endTime: endTime ?? '',
        dateString: `${dateParts[2]}-${dateParts[0]}-${dateParts[1]}`, //yyyy-MM-DD
        startTimeMeridiemFieldOption: (startTimeMeridiemFieldOption ??
            '') as MeridiemFieldValue,
        endTimeMeridiemFieldOption: (endTimeMeridiemFieldOption ??
            '') as MeridiemFieldValue,
        instructors: instructors ?? [],
    };
};

export const getHourTimeFormat = (
    dateTime: number | null,
    timeZone: string = 'UTC',
) => {
    const localeDate = dateTime ? new Date(dateTime * 1000) : new Date();
    let localeDateTime = '';
    try {
        localeDateTime = localeDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timeZone,
        });
    } catch (e) {
        // if timezone is invalid for some reason (very rare)
        // gracefully handling timezone
        localeDateTime = localeDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'America/Los_Angeles',
        });
    }
    const timeParts = localeDateTime.split(' ');
    return {
        time: timeParts[0],
        meridiem: timeParts[1],
    };
};

export const formatNumber = (number?: any): number => {
    if (number) {
        return !isNaN(number) ? number : 0;
    } else {
        return 0;
    }
};

export const formatGrimsbyDate = (
    timeStamp: number | null,
    timeZone = 'UTC',
) => {
    if (timeStamp == null) {
        return EMPTY_STRING;
    }
    const sessionTime = new Date(timeStamp * 1000);
    let localeSessionDate = '';
    try {
        localeSessionDate = sessionTime.toLocaleDateString('en-US', {
            timeZone: timeZone,
            month: 'long',
            day: '2-digit',
            year: 'numeric',
        });
    } catch (e) {
        localeSessionDate = sessionTime.toLocaleDateString('en-US', {
            timeZone: 'America/Los_Angeles',
            month: 'long',
            day: '2-digit',
            year: 'numeric',
        });
    }

    return localeSessionDate;
};

export const formatDeliverySessionTime = (
    {
        start_timestamp: startTimeStamp,
        end_timestamp: endTimeStamp,
    }: DeliverySession,
    deliveryTimeZone: string,
) => {
    if (startTimeStamp == null || endTimeStamp == null) {
        return EMPTY_STRING;
    }
    const { time: startTime, meridiem: startMeridiem } = getHourTimeFormat(
        startTimeStamp,
        deliveryTimeZone,
    );
    const { time: endTime, meridiem: endMeridiem } = getHourTimeFormat(
        endTimeStamp,
        deliveryTimeZone,
    );

    return `${startTime}${startMeridiem?.toLocaleLowerCase()} - ${endTime}${endMeridiem?.toLocaleLowerCase()}`;
};

export const getActivityStartAndEndFromSessions = (
    deliverySessions: Array<DeliverySession>,
    timezone: string,
) => {
    deliverySessions.sort(
        (a, b) => (a.start_timestamp || 0) - (b.start_timestamp || 0),
    );

    const a = deliverySessions[0];
    const b = deliverySessions[deliverySessions.length - 1];
    const startTimestamp = a?.start_timestamp || 0;
    const endTimestamp = b?.end_timestamp || 0;
    return {
        start:
            convertTimeStampToLocalUnix({
                timestamp: startTimestamp,
                timezone,
            }) * 1000,
        end:
            convertTimeStampToLocalUnix({ timestamp: endTimestamp, timezone }) *
            1000,
    };
};

export const getActivityStartDateFromSessions = (
    deliverySessions: Array<DeliverySession>,
    deliveryTimezone: string,
) => {
    return getActivityStartAndEndFromSessions(
        deliverySessions,
        deliveryTimezone,
    ).start;
};

export const isACI = (activityGroupName, isACIFeatureEnabled) =>
    isACIFeatureEnabled && activityGroupName;

export const getEditButtonProps = ({
    activity,
    testId,
    path,
    isACIFeatureEnabled,
}: {
    activity: ActivityData;
    testId: string;
    path: string;
    isACIFeatureEnabled: boolean;
}) => ({
    activityStatus: activity?.activity_status,
    testId,
    lmsType: activity?.lms_type,
    path: `${activity.pk}/${path}`,
    isACIFeatureEnabled,
    activityGroupName: activity?.activity_group_name,
});

export const showEditButton = (activity: {
    activity_status: string;
}): boolean => {
    if (activity?.activity_status === ActivityStatus.Canceled) {
        return false;
    } else return true;
};

export const isPartnerPrivateCustomer = (activityAudience: string) => {
    return (
        activityAudience === AudienceType.PartnerPrivate ||
        activityAudience === AudienceType.PartnerSemiPrivate ||
        activityAudience === AudienceType.PartnerPublicUnlisted
    );
};

export const isCommercialPrivateCustomer = (activityAudience: string) => {
    return (
        activityAudience === AudienceType.CommercialPrivate ||
        activityAudience === AudienceType.CommercialSemiPrivate
    );
};

export const isInternalPrivateCustomer = (activityAudience: string) => {
    return activityAudience === AudienceType.InternalPrivate;
};

export const isPublicCustomer = (activityAudience: string) => {
    return (
        activityAudience === AudienceType.Public ||
        activityAudience === AudienceType.PublicUnlisted
    );
};

export const DATE_RANGE_FILTER_KEY = 'dateRange';

export const isValidEvcspLink = (url: string): boolean => {
    if (!url) return true; // Empty is considered valid (will be caught by required field validation)
    const evcspPattern = /^https:\/\/(?:.*\.)?evcsp\.|^https:\/\/.*\.evcsp\./i;
    return evcspPattern.test(url);
};

export interface VILTLinkFieldState {
    isDisabled: boolean;
    infoText?: string;
    placeholder?: string;
}

export const getVILTLinkFieldState = (
    dateString: string,
    timeString: string,
    timezone: string,
    isViltOrHybrid: boolean,
    vIltType?: string,
    currentViltLink?: string,
    lmsType?: string,
): VILTLinkFieldState => {
    if (!isViltOrHybrid) {
        return { isDisabled: false };
    }

    if (!dateString || !timeString) {
        return { isDisabled: false };
    }

    // "Other" vILT type allows manual entry for ALL LMS types
    // This supports use cases like Zoom/Teams for any customer configuration
    // Updated per VILT_Other_Type_Findings: extend manual entry to all LMS types
    if (vIltType === ActivityViltType.Other) {
        return {
            isDisabled: false,
            infoText:
                'Manual vILT entry - manage meeting details outside Grimsby',
            placeholder: 'Enter vILT meeting link (e.g., Zoom, Teams URL)',
        };
    }

    // For WebEx type, check if we have a valid EVCSP link
    if (vIltType === ActivityViltType.WebEx) {
        // If there's a valid EVCSP link, it cannot be updated
        if (currentViltLink && isValidEvcspLink(currentViltLink)) {
            return {
                isDisabled: true,
                infoText: '',
                placeholder: currentViltLink,
            };
        }

        const sessionDateTime = dayjs.tz(
            `${dateString} ${timeString}`,
            timezone,
        );
        const isPastDate = sessionDateTime.isBefore(dayjs());

        if (isPastDate) {
            return {
                isDisabled: false,
                infoText: 'Manual vILT entry for past dates',
                placeholder: 'Enter vILT meeting details',
            };
        }

        // For future dates with WebEx (no EVCSP link yet), disable and show auto-generation message
        return {
            isDisabled: true,
            infoText:
                'vILT details will be auto-generated when activity is active',
            placeholder: 'Auto-generated on activity activation',
        };
    }

    // For non-WebEx vILT types, allow manual entry
    return { isDisabled: false };
};

export const getEarliestSessionDate = (
    deliverySessions: Array<{ start_timestamp: number }>,
    timezone: string,
): { dateString: string; timeString: string } | null => {
    if (!deliverySessions?.length) return null;

    const earliestSession = [...deliverySessions].sort(
        (a, b) => a.start_timestamp - b.start_timestamp,
    )[0];

    const sessionDate = dayjs.tz(
        dayjs.unix(earliestSession.start_timestamp),
        timezone,
    );

    return {
        dateString: sessionDate.format('YYYY-MM-DD'),
        timeString: sessionDate.format('HH:mm'),
    };
};

/**
 * Get the effective LMS ID for display - optimistically uses pk for myClass
 */
export const getEffectiveLmsId = (activity: ActivityData): string | null => {
    if (activity.lms_type === ActivityLMSType.MyClass && !activity.lms_id) {
        return activity.pk;
    }
    return activity.lms_id || null;
};

/**
 * LMS types for which attended/registered counts are
 * auto-populated from AMS/RMS. User input for these fields is disabled.
 */
const AMS_RMS_AUTO_COUNT_LMS_TYPES = new Set([
    ActivityLMSType.MyClass,
    ActivityLMSType.SkillBuilder,
]);

export const isAmsRmsAutoCountLmsType = (
    lmsType: string | undefined | null,
): boolean =>
    !!lmsType && AMS_RMS_AUTO_COUNT_LMS_TYPES.has(lmsType as ActivityLMSType);

/**
 * Get the SkillBuilder (TC3) base URL for the current environment stage.
 */
export const getSkillBuilderBaseUrl = (): string => {
    try {
        const config = window.__CONFIGURATION__ || {};
        const stage = ((config as any).STAGE || 'prod').toLowerCase();
        const stageUrls: { [key: string]: string } = {
            prod: 'https://skillbuilder.aws',
            gamma: 'https://skillbuilder.training.aws.dev',
            beta: 'https://beta.skillbuilder.training.aws.dev',
            sam: 'https://beta.skillbuilder.training.aws.dev',
        };

        return stageUrls[stage] || stageUrls.prod;
    } catch (error) {
        return 'https://skillbuilder.aws';
    }
};

/**
 * Get the LMS registration link based on type and organization
 */
export const getLmsRegistrationLink = (
    activity: ActivityData,
): string | null => {
    const effectiveLmsId = getEffectiveLmsId(activity);
    if (!effectiveLmsId) return null;

    if (activity.lms_type === ActivityLMSType.MyClass) {
        const baseUrl = getSkillBuilderBaseUrl();
        return `${baseUrl}/admin/organization/modality/curriculum/training/${effectiveLmsId}`;
    }

    // For other types, use existing logic
    return null;
};

/**
 * Check if activity is a semi-private activity type.
 * Semi-private activities have class size auto-calculated from customer commitments.
 */
export const isSemiPrivateActivity = (activity?: ActivityData): boolean => {
    if (!activity?.activity_audience) return false;
    return [
        AudienceType.CommercialSemiPrivate,
        AudienceType.PartnerSemiPrivate,
    ].includes(activity.activity_audience as AudienceType);
};

/**
 * Check if activity requires customer commitments validation.
 * This applies only to Commercial Private and Commercial Semi-Private activities,
 * as these are the activity types where customers must be defined with commitments
 * that align with class size.
 */
export const requiresCustomerCommitmentsValidation = (
    activity?: ActivityData,
): boolean => {
    if (!activity?.activity_audience) return false;
    return [
        AudienceType.CommercialPrivate,
        AudienceType.CommercialSemiPrivate,
    ].includes(activity.activity_audience as AudienceType);
};

/**
 * Validate class size against sum of customer commitments.
 * Only validates for Commercial Private and Commercial Semi-Private activities.
 */
export const validateClassSizeConsistency = (
    activity?: ActivityData,
): { isValid: boolean; sum: number; classSize: number | null } => {
    if (!activity || !requiresCustomerCommitmentsValidation(activity)) {
        return {
            isValid: true,
            sum: 0,
            classSize: activity?.class_size ?? null,
        };
    }

    const sum = (activity.customers || []).reduce((total, customer) => {
        const committed = (customer as any).number_of_students_committed;
        return total + (committed || 0);
    }, 0);

    const classSize = activity.class_size ?? null;
    const isValid = classSize === null || sum === classSize;

    return { isValid, sum, classSize };
};

/**
 * Validate that every customer on a customer-scoped myClass activity has an SFDC account ID before
 * the activity can be set to Active. For Commercial Private / Commercial Semi-Private myClass
 * activities, registration eligibility keys on the customer's sfdc_account_id; without it learners
 * can see the class but cannot enroll. The schedule service enforces this server-side
 * (ClientSideErrorCodes.MYCLASS_CUSTOMER_ACCOUNT_FIELDS_MISSING); validating client-side gives the
 * scheduler immediate, actionable feedback before the save round-trips.
 *
 * Only applies to Commercial Private / Commercial Semi-Private myClass activities being set to
 * Active. Partner / public / team-bound / invite-only myClass activities register via other
 * eligibility paths and are not gated.
 */
export const validateMyClassCustomerAccountFields = (
    activity?: ActivityData,
): boolean => {
    if (
        !activity ||
        activity.lms_type !== ActivityLMSType.MyClass ||
        activity.activity_status !== ActivityStatus.Active ||
        !isCommercialPrivateCustomer(activity.activity_audience || '')
    ) {
        return true;
    }

    const customers = activity.customers || [];
    if (customers.length === 0) {
        return false;
    }

    return customers.every((customer) => !!customer?.sfdc_account_id);
};
